import searchAtomPair, { SearchAtom } from 'atoms/search-atom';
import { Column, Row, ComboBox, FlexGrid as Grid } from '@carbon/react';
import { FormRow, FormSection } from 'components/forms';
import { TextField } from 'components/form-fields/text-field';
import { DateField } from 'components/form-fields/date-field';
import { useAtom } from 'jotai';
import { debounce } from 'lodash-es';
import { useEffect, useMemo, useRef, useState } from 'react';
import { CountyQueryResponse, DocumentResponse } from 'types/api-responses';
import { AtomizedDocumentEditForm } from './types';
import { SourceDocumentsSection } from './source-documents-section';
import style from './edit-document-modal.module.scss';

const { searchAtom } = searchAtomPair<CountyQueryResponse>('counties');

type SelectedCounty = Omit<CountyQueryResponse, 'id'> & { id: number | null };

type CountyComboBoxProps = {
  counties: SearchAtom<CountyQueryResponse>;
  selectedCounty: SelectedCounty;
  setCountyId: (id: number | null) => void;
  setSelectedCounty: (county: SelectedCounty) => void;
  debouncedSetCounties: ReturnType<typeof debounce>;
};

const CountyComboBox = ({
  counties,
  selectedCounty,
  setCountyId,
  setSelectedCounty,
  debouncedSetCounties,
}: CountyComboBoxProps) => (
  <ComboBox
    size="md"
    id="county"
    titleText="County"
    items={counties?.data?.results || []}
    placeholder=""
    itemToString={(data) => {
      return data?.name && data?.stateAbbreviation
        ? `${data.name}, ${data.stateAbbreviation}`
        : '';
    }}
    selectedItem={selectedCounty}
    itemToElement={(result) => (
      <>{`${result.name}, ${result.stateAbbreviation}`}</>
    )}
    onChange={({ selectedItem }) => {
      const county = counties?.data?.results?.find(
        (result) => result.id === selectedItem?.id
      );
      if (county) {
        setCountyId(county.id);
        setSelectedCounty(county);
      } else {
        setCountyId(null);
        setSelectedCounty({
          id: null,
          name: '',
          stateAbbreviation: '',
          fips: '',
        });
      }
    }}
    onInputChange={(text) => {
      debouncedSetCounties((current: SearchAtom<CountyQueryResponse>) => ({
        ...current,
        text,
        pageSize: 10,
      }));
    }}
  />
);

export const EditDocumentForm = ({
  form,
  type,
  county,
  sourceDocuments,
}: {
  form: AtomizedDocumentEditForm;
  type: DocumentResponse['type'];
  county: DocumentResponse['county'];
  sourceDocuments: DocumentResponse['sourceDocuments'];
}) => {
  const isSubsequentRender = useRef(false);
  const [counties, setCounties] = useAtom(searchAtom);
  const debouncedSetCounties = useMemo(
    () => debounce(setCounties, 400),
    [setCounties]
  );
  const [countyId, setCountyId] = useAtom(form.countyId);
  const [selectedCounty, setSelectedCounty] = useState<
    Omit<CountyQueryResponse, 'id'> & { id: number | null }
  >({
    id: null,
    name: county?.name || '',
    stateAbbreviation: county?.state.abbreviation || '',
    fips: '',
  });

  // v11 fix: ComboBox no longer fires onInputChange on mount, so the
  // county search never triggers. Fetch the initial county by name so
  // the useEffect below can resolve the county ID.
  useEffect(() => {
    if (county?.name && !counties.data?.results) {
      setCounties((current) => ({
        ...current,
        text: county.name,
        pageSize: 10,
      }));
    }
  }, [county?.name, counties.data?.results, setCounties]);

  useEffect(() => {
    if (
      selectedCounty.id === null &&
      countyId?.value === null &&
      counties.data?.results
    ) {
      if (!isSubsequentRender.current) {
        setCountyId(
          counties.data.results.find(
            (result) => result.name === selectedCounty?.name
          )?.id ?? null
        );
        isSubsequentRender.current = true;
      }
    }
  }, [
    selectedCounty.id,
    countyId?.value,
    counties.data?.results,
    setCountyId,
    selectedCounty?.name,
  ]);

  const countyComboBoxProps = {
    counties,
    selectedCounty,
    setCountyId,
    setSelectedCounty,
    debouncedSetCounties,
  };

  return (
    <>
      <EditDocumentDates type={type} form={form} />
      {type === 'Document::Courthouse' && (
        <>
          <FormRow>
            <Grid className="bx--no-gutter" fullWidth>
              <Row className={style.rowThirds}>
                <Column>
                  <CountyComboBox {...countyComboBoxProps} />
                </Column>
                <Column>
                  <TextField
                    fieldAtom={form.bookType}
                    id="book-type"
                    labelText="Book Type"
                  />
                </Column>
                <Column>
                  <TextField
                    fieldAtom={form.documentType}
                    id="document-type"
                    labelText="Document Type"
                  />
                </Column>
              </Row>
              <Row className={style.rowThirds}>
                <Column>
                  <TextField
                    id="document-volume"
                    fieldAtom={form.volume}
                    labelText="Volume"
                  />
                </Column>
                <Column>
                  <TextField
                    id="document-page"
                    fieldAtom={form.page}
                    labelText="Page"
                  />
                </Column>
                <Column>
                  <TextField
                    id="document-instrument-number"
                    fieldAtom={form.instrumentNumber}
                    labelText="Instrument Number"
                  />
                </Column>
              </Row>
            </Grid>
          </FormRow>
        </>
      )}
      {type === 'Document::Regulatory' && (
        <FormRow>
          <Grid className="bx--no-gutter" fullWidth>
            <Row className={style.rowThirds}>
              <Column>
                <CountyComboBox {...countyComboBoxProps} />
              </Column>
              <Column>
                <TextField
                  fieldAtom={form.documentType}
                  id="document-type"
                  labelText="Document Type"
                />
              </Column>
              <Column />
            </Row>
          </Grid>
        </FormRow>
      )}
      {type === 'Document::DivisionOrder' && (
        <FormRow>
          <Grid className="bx--no-gutter" fullWidth>
            <Row className={style.rowThirds}>
              <Column>
                <CountyComboBox {...countyComboBoxProps} />
              </Column>
              <Column>
                <TextField
                  fieldAtom={form.documentType}
                  id="document-type"
                  labelText="Document Type"
                />
              </Column>
              <Column>
                <TextField
                  id="document-instrument-number"
                  fieldAtom={form.instrumentNumber}
                  labelText="DO Number"
                />
              </Column>
            </Row>
          </Grid>
        </FormRow>
      )}
      {type === 'Document::Afe' && (
        <FormRow>
          <Grid className="bx--no-gutter" fullWidth>
            <Row className={style.rowThirds}>
              <Column>
                <CountyComboBox {...countyComboBoxProps} />
              </Column>
              <Column>
                <TextField
                  fieldAtom={form.documentType}
                  id="document-type"
                  labelText="AFE Type"
                />
              </Column>
              <Column>
                <TextField
                  id="document-instrument-number"
                  fieldAtom={form.instrumentNumber}
                  labelText="AFE Number"
                />
              </Column>
            </Row>
          </Grid>
        </FormRow>
      )}
      {type === 'Document::RoyaltyCheck' && (
        <FormRow>
          <Grid className="bx--no-gutter" fullWidth>
            <Row className={style.rowThirds}>
              <Column>
                <CountyComboBox {...countyComboBoxProps} />
              </Column>
              <Column>
                <TextField
                  fieldAtom={form.documentType}
                  id="document-type"
                  labelText="Document Type"
                />
              </Column>
              <Column>
                <TextField
                  id="document-instrument-number"
                  fieldAtom={form.instrumentNumber}
                  labelText="Check Number"
                />
              </Column>
            </Row>
          </Grid>
        </FormRow>
      )}
      {type === 'Document::Courthouse' && sourceDocuments && (
        <SourceDocumentsSection sourceDocuments={sourceDocuments} />
      )}
    </>
  );
};

const EditDocumentDates = ({
  type,
  form,
}: {
  type: DocumentResponse['type'];
  form: AtomizedDocumentEditForm;
}) => {
  return (
    <FormSection>
      {type === 'Document::Courthouse' && (
        <>
          <FormRow>
            <Row>
              <Column>
                <DateField
                  size="md"
                  id="effective-date"
                  labelText="Effective Date"
                  field={form.effectiveDate}
                />
              </Column>
              <Column>
                <DateField
                  size="md"
                  id="instrument-date"
                  labelText="Instrument Date"
                  field={form.instrumentDate}
                />
              </Column>
            </Row>
          </FormRow>
          <FormRow>
            <Row>
              <Column>
                <DateField
                  size="md"
                  id="file-date"
                  labelText="File Date"
                  field={form.fileDate}
                />
              </Column>
              <Column>
                <DateField
                  size="md"
                  id="recorded-date"
                  labelText="Recorded Date"
                  field={form.recordedDate}
                />
              </Column>
            </Row>
          </FormRow>
        </>
      )}
      {type === 'Document::NonCourthouse' && (
        <FormRow>
          <Row>
            <Column sm={8} md={4} lg={8}>
              <DateField
                size="md"
                id="effective-date"
                labelText="Effective Date"
                field={form.effectiveDate}
              />
            </Column>
          </Row>
        </FormRow>
      )}
      {(type === 'Document::Regulatory' ||
        type === 'Document::Afe' ||
        type === 'Document::DivisionOrder' ||
        type === 'Document::RoyaltyCheck') && (
        <FormRow>
          <Row>
            <Column>
              <DateField
                size="md"
                id="effective-date"
                labelText="Effective Date"
                field={form.effectiveDate}
              />
            </Column>
            <Column>
              <DateField
                size="md"
                id="file-date"
                labelText="File Date"
                field={form.fileDate}
              />
            </Column>
          </Row>
        </FormRow>
      )}
    </FormSection>
  );
};
